import { useState } from 'react';
import type { FormField } from '@/types/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Trash2, Settings, Eye } from 'lucide-react';

interface FieldEditorProps {
  field: FormField;
  onUpdateField: (updates: Partial<FormField>) => void;
  onClose: () => void;
}

export const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  onUpdateField,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'options' | 'validation' | 'logic'>('basic');

  const handleBasicUpdate = (key: keyof FormField, value: any) => {
    onUpdateField({ [key]: value });
  };

  const handleAddOption = () => {
    const newOptions = [
      ...(field.options || []),
      { value: `option_${Date.now()}`, label: 'New Option' }
    ];
    onUpdateField({ options: newOptions });
  };

  const handleUpdateOption = (index: number, key: 'value' | 'label', value: string) => {
    if (!field.options) return;
    
    const newOptions = [...field.options];
    newOptions[index] = { ...newOptions[index], [key]: value };
    onUpdateField({ options: newOptions });
  };

  const handleDeleteOption = (index: number) => {
    if (!field.options) return;
    
    const newOptions = field.options.filter((_, i) => i !== index);
    onUpdateField({ options: newOptions });
  };

  const handleValidationUpdate = (key: string, value: any) => {
    const newValidation = { ...field.validation, [key]: value };
    onUpdateField({ validation: newValidation });
  };

  const hasOptions = ['select', 'radio', 'checkbox'].includes(field.type);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">Field Settings</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-xs">
                {field.type}
              </Badge>
              <span className="text-sm text-muted-foreground">{field.name}</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          <Button
            variant={activeTab === 'basic' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('basic')}
          >
            <Settings className="h-3 w-3 mr-1" />
            Basic
          </Button>
          {hasOptions && (
            <Button
              variant={activeTab === 'options' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('options')}
            >
              Options
            </Button>
          )}
          <Button
            variant={activeTab === 'validation' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('validation')}
          >
            Validation
          </Button>
          <Button
            variant={activeTab === 'logic' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('logic')}
          >
            Logic
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'basic' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Basic Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Field Name</label>
                  <Input
                    value={field.name}
                    onChange={(e) => handleBasicUpdate('name', e.target.value)}
                    placeholder="field_name"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Unique identifier for this field (no spaces)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Label</label>
                  <Input
                    value={field.label}
                    onChange={(e) => handleBasicUpdate('label', e.target.value)}
                    placeholder="Field Label"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Placeholder</label>
                  <Input
                    value={field.placeholder || ''}
                    onChange={(e) => handleBasicUpdate('placeholder', e.target.value)}
                    placeholder="Enter placeholder text..."
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <textarea
                    value={field.description || ''}
                    onChange={(e) => handleBasicUpdate('description', e.target.value)}
                    placeholder="Field description (optional)"
                    className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={field.required || false}
                      onChange={(e) => handleBasicUpdate('required', e.target.checked)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium">Required field</span>
                  </label>
                </div>
              </CardContent>
            </Card>

            {(['input', 'textarea', 'number'].includes(field.type)) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Default Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    value={field.default as string || ''}
                    onChange={(e) => handleBasicUpdate('default', e.target.value)}
                    placeholder="Default value"
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}

        {activeTab === 'options' && hasOptions && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Options</CardTitle>
              <Button size="sm" onClick={handleAddOption}>
                <Plus className="h-3 w-3 mr-1" />
                Add Option
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {field.options?.map((option, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Option value"
                        value={option.value}
                        onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                      />
                      <Input
                        placeholder="Option label"
                        value={option.label}
                        onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteOption(index)}
                      className="mt-0 shrink-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                
                {(!field.options || field.options.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="text-sm">No options added yet</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddOption}
                      className="mt-2"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add First Option
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'validation' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Validation Rules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(['input', 'textarea'].includes(field.type)) && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Min Length</label>
                      <Input
                        type="number"
                        value={field.validation?.minLength || ''}
                        onChange={(e) => handleValidationUpdate('minLength', parseInt(e.target.value) || undefined)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Max Length</label>
                      <Input
                        type="number"
                        value={field.validation?.maxLength || ''}
                        onChange={(e) => handleValidationUpdate('maxLength', parseInt(e.target.value) || undefined)}
                        placeholder="100"
                      />
                    </div>
                  </div>
                </>
              )}

              {field.type === 'number' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Min Value</label>
                    <Input
                      type="number"
                      value={field.validation?.min || ''}
                      onChange={(e) => handleValidationUpdate('min', parseInt(e.target.value) || undefined)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Max Value</label>
                    <Input
                      type="number"
                      value={field.validation?.max || ''}
                      onChange={(e) => handleValidationUpdate('max', parseInt(e.target.value) || undefined)}
                      placeholder="100"
                    />
                  </div>
                </div>
              )}

              {field.type === 'file' && (
                <>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Max File Size (MB)</label>
                    <Input
                      type="number"
                      value={field.validation?.maxSizeBytes ? (field.validation.maxSizeBytes / 1048576).toFixed(1) : '10'}
                      onChange={(e) => {
                        const mb = parseFloat(e.target.value) || 10;
                        handleValidationUpdate('maxSizeBytes', Math.round(mb * 1048576));
                      }}
                      placeholder="10"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Allowed File Types</label>
                    <div className="space-y-2">
                      <Input
                        value={(field.validation?.allowedTypes || []).join(', ')}
                        onChange={(e) => {
                          const types = e.target.value
                            .split(',')
                            .map(type => type.trim())
                            .filter(type => type.length > 0);
                          handleValidationUpdate('allowedTypes', types.length > 0 ? types : ['*/*']);
                        }}
                        placeholder="image/*, application/pdf, .doc, .docx"
                      />
                      <p className="text-xs text-muted-foreground">
                        Separate multiple types with commas. Use MIME types (image/*) or extensions (.pdf)
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="text-sm font-medium mb-1 block">Pattern (RegEx)</label>
                <Input
                  value={field.validation?.pattern || ''}
                  onChange={(e) => handleValidationUpdate('pattern', e.target.value)}
                  placeholder="^[a-zA-Z0-9]+$"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Error Message</label>
                <Input
                  value={field.validation?.message || ''}
                  onChange={(e) => handleValidationUpdate('message', e.target.value)}
                  placeholder="Please enter a valid value"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'logic' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conditional Logic</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <div className="text-sm mb-2">Conditional logic coming soon</div>
                <p className="text-xs">Show/hide fields based on other field values</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Preview */}
      <div className="border-t border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-4 w-4" />
          <span className="text-sm font-medium">Preview</span>
        </div>
        
        <div className="bg-muted/30 p-3 rounded-md">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              {field.type} field preview
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};